import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'
import { conversationHeaderListEquality } from '@/chat-common/conversation_list/util'
import { AgentV2ConversationSurface, type AgentV2ClientSurface } from '@/agent-v2/store/schemas'
import { selectUnavailableUserFileIds } from '@/user_files/files/store/selectors'
import { AgentV2ConversationDeletionStatus, AgentV2RunPhase, type AgentV2Conversation } from './slice'
import { isOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'

export const selectAgentV2HostSurface = (state: RootState) => state.agentV2ConversationsState.hostSurface

/**
 * Browser sessions may continue saved Outlook conversations. Word and Outlook hosts retain
 * their own origin scope. An absent origin is a historical browser conversation.
 */
export function isAgentV2ConversationOwnedByHost(originSurface: AgentV2ConversationSurface | undefined, hostSurface: AgentV2ClientSurface): boolean {
  const origin = originSurface ?? AgentV2ConversationSurface.WEB
  return origin === (hostSurface as string) || (hostSurface === 'web' && origin === AgentV2ConversationSurface.OUTLOOK)
}

export const selectAgentV2ConversationFromId = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.conversations[props.conversationId ?? ''] ?? null

export const selectAgentV2ConversationExists = createSelector(selectAgentV2ConversationFromId, (conversation) => conversation !== null)

export const selectAgentV2ConversationMatterId = createSelector(selectAgentV2ConversationFromId, (conversation) => conversation?.matter_id ?? null)

export const selectAgentV2ConversationDetail = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.details[props.conversationId ?? ''] ?? null

export const selectAgentV2ConversationHistory = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.historyByConversationId[props.conversationId ?? '']

export const selectAgentV2TurnFeedback = (state: RootState, props: { turnId: string | undefined }) =>
  state.agentV2ConversationsState.feedbackByTurnId[props.turnId ?? ''] ?? null

export const selectAgentV2ConversationLoading = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.detailLoadingByConversationId[props.conversationId ?? ''] ?? false

/**
 * Whether a conversation's first successful load is still in flight while the chat's files wait on it. Once a sent turn
 * has set them, the load has nothing to seed.
 */
export const selectAgentV2ConversationFirstLoadPending = (state: RootState, props: { conversationId: string | undefined }) =>
  selectAgentV2ConversationLoading(state, props) &&
  (state.agentV2ConversationsState.detailLastRefreshByConversationId[props.conversationId ?? ''] ?? null) === null &&
  state.agentV2ConversationsState.chatFilesByConversationId[props.conversationId ?? ''] === undefined

const kNoChatFileIds: string[] = []

/** The files the chat sends again with every next turn: what the latest turn sent, minus any removed since. */
export const selectAgentV2ConversationChatFileIds = (state: RootState, props: { conversationId: string }) =>
  state.agentV2ConversationsState.chatFilesByConversationId[props.conversationId]?.fileIds ?? kNoChatFileIds

/** The writing sample the chat sends again with every next turn, unless the composer picks another. */
export const selectAgentV2ConversationChatWritingSampleFileId = (state: RootState, props: { conversationId: string }) =>
  state.agentV2ConversationsState.chatFilesByConversationId[props.conversationId]?.writingSampleFileId ?? null

/** The chat's files that can still be sent. A deleted file would fail the whole turn, so it's left out. */
export const selectAgentV2SendableChatFileIds = createSelector(
  [selectAgentV2ConversationChatFileIds, selectUnavailableUserFileIds],
  (chatFileIds, unavailableFileIds) => chatFileIds.filter((fileId) => !unavailableFileIds[fileId])
)

/** The chat's writing sample, unless it has been deleted. */
export const selectAgentV2SendableChatWritingSampleFileId = (state: RootState, props: { conversationId: string }) => {
  const writingSampleFileId = selectAgentV2ConversationChatWritingSampleFileId(state, props)
  return writingSampleFileId && !selectUnavailableUserFileIds(state)[writingSampleFileId] ? writingSampleFileId : null
}

export const selectAgentV2ConversationLoadError = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.detailErrorByConversationId[props.conversationId ?? ''] ?? null

export const selectAgentV2ConversationDeletionStatus = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.deletionStatusByConversationId[props.conversationId ?? '']

export const selectAgentV2ConversationKnownDeleted = (state: RootState, props: { conversationId: string | undefined }) =>
  selectAgentV2ConversationDeletionStatus(state, props) === AgentV2ConversationDeletionStatus.DELETED

export const selectAgentV2RunPhase = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.runLifecycleByConversationId[props.conversationId ?? '']?.phase ?? AgentV2RunPhase.IDLE

export const selectAgentV2StopRequested = (state: RootState, props: { conversationId: string | undefined }) =>
  state.agentV2ConversationsState.runLifecycleByConversationId[props.conversationId ?? '']?.stopRequested ?? false

/**
 * Excludes other members' empty placeholders while keeping the viewer's own
 * placeholder and every conversation with durable activity admitted on the current host.
 */
export const selectAgentV2ConversationsByMatter = createSelector(
  (state: RootState) => state.agentV2ConversationsState.conversations,
  (state: RootState) => state.agentV2ConversationsState.listedConversationIdsByMatterId,
  selectAgentV2HostSurface,
  (_: RootState, props: { matterId: string | null; currentUserId: string | undefined }) => props.matterId,
  (_: RootState, props: { matterId: string | null; currentUserId: string | undefined }) => props.currentUserId,
  (conversationsById, listedConversationIdsByMatterId, hostSurface, matterId, currentUserId) => {
    if (!matterId) return []
    return (listedConversationIdsByMatterId[matterId] ?? [])
      .map((conversationId) => conversationsById[conversationId])
      .filter(
        (conversation): conversation is AgentV2Conversation =>
          conversation?.matter_id === matterId &&
          isAgentV2ConversationOwnedByHost(conversation.origin_surface, hostSurface) &&
          (!currentUserId || conversation.created_by_user_id === currentUserId || conversation.has_activity)
      )
      .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
      .map((conversation) =>
        hostSurface === 'web' && conversation.origin_surface === AgentV2ConversationSurface.OUTLOOK && !isOutlookPrivacyMode()
          ? { ...conversation, title: 'Outlook conversation' }
          : conversation
      )
  }
)

/**
 * Recent-activity-specific view of a matter's conversations.
 */
export const selectAgentV2ConversationsWithActivityByMatter = createSelector(selectAgentV2ConversationsByMatter, (conversations) =>
  conversations.filter((conversation) => conversation.has_activity)
)

/**
 * Returns the `ChatListSidebarItem`s for a matter's conversations, restricted to those
 * created by `currentUserId`, and drops empty (no-activity) placeholders entirely — the side nav's
 * own "New Chat" action row is the entry point, so an unused placeholder is just clutter there.
 * Used by the side nav's Chat History section, which only lists the current user's own chats,
 * not read-only chats shared by matter collaborators.
 */
export const selectOwnedAgentV2ChatListSidebarItems = createSelector(
  selectAgentV2ConversationsByMatter,
  (state: RootState) => state.agentV2ConversationsState.unreadResponseByConversationId,
  (state: RootState) => state.agentV2ConversationsState.conversations,
  (state: RootState, props: { matterId: string | null }) =>
    props.matterId ? state.agentV2ConversationsState.sidebarListsByMatterId[props.matterId] : undefined,
  (_: RootState, props: { matterId: string | null; currentUserId: string | undefined }) => props.currentUserId,
  selectAgentV2HostSurface,
  (conversations, unreadResponseByConversationId, entities, sidebar, currentUserId, hostSurface): ChatListSidebarItem[] =>
    (sidebar?.loaded
      ? sidebar.ids
          .map((id) => entities[id])
          .filter((conversation): conversation is AgentV2Conversation => Boolean(conversation))
          .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
      : conversations
    )
      .filter(
        (conversation) =>
          isAgentV2ConversationOwnedByHost(conversation.origin_surface, hostSurface) &&
          (!currentUserId || conversation.created_by_user_id === currentUserId) &&
          conversation.has_activity
      )
      .map((conversation) => ({
        id: conversation.id,
        title:
          hostSurface === 'web' && conversation.origin_surface === AgentV2ConversationSurface.OUTLOOK && !isOutlookPrivacyMode()
            ? 'Outlook conversation'
            : conversation.title,
        matter_id: conversation.matter_id,
        modified_on: Date.parse(conversation.updated_at),
        unread: unreadResponseByConversationId[conversation.id] ?? false,
      })),
  // Compare displayed headers because unrelated conversation updates can change the upstream array reference.
  { memoizeOptions: { resultEqualityCheck: conversationHeaderListEquality } }
)

export const selectAgentV2HasConversationsForMatter = createSelector(selectAgentV2ConversationsByMatter, (conversations) => conversations.length > 0)

export const selectMostRecentAgentV2NewChat = createSelector(
  selectAgentV2ConversationsByMatter,
  (conversations) => conversations.find((conversation) => conversation.title === kNewConversationTitle) ?? null
)

/**
 * The viewer's most recent chat in a matter that no message has been sent in yet.
 */
export const selectUnsentAgentV2Chat = createSelector(
  selectAgentV2ConversationsByMatter,
  (state: RootState) => state.agentV2ConversationsState.runLifecycleByConversationId,
  (state: RootState) => state.agentV2ConversationsState.details,
  (conversations, runLifecycleByConversationId, details) =>
    conversations.find((conversation) => {
      // has_activity can lag behind (it only updates when the list is refetched), so a run started here (or one that already
      // finished) or loaded turns also count as sent.
      const runLifecycle = runLifecycleByConversationId[conversation.id]
      const hasLocalRun = runLifecycle ? runLifecycle.phase !== AgentV2RunPhase.IDLE || runLifecycle.turnId !== null : false
      const hasLoadedTurns = (details[conversation.id]?.turns.length ?? 0) > 0
      return conversation.title === kNewConversationTitle && !conversation.has_activity && !hasLocalRun && !hasLoadedTurns
    }) ?? null
)

export const selectAgentV2ListLoading = (state: RootState, props: { matterId: string | null }) =>
  props.matterId ? state.agentV2ConversationsState.loadingByMatterId[props.matterId] ?? false : false

export const selectAgentV2ListError = (state: RootState, props: { matterId: string | null }) =>
  props.matterId ? state.agentV2ConversationsState.errorByMatterId[props.matterId] ?? false : false

export const selectAgentV2InitialLoadComplete = (state: RootState, props: { matterId: string | null }) =>
  props.matterId ? state.agentV2ConversationsState.initialLoadCompleteByMatterId[props.matterId] ?? false : false

export const selectAgentV2FollowUpQuestions = (state: RootState, props: { assistantMessageId: string | null }) =>
  props.assistantMessageId ? state.agentV2ConversationsState.followUpQuestionsByAssistantMessageId[props.assistantMessageId] ?? null : null
